import { useContext, useReducer, useRef, useState } from "react";

// Context import
import { AppContext } from "../../../../../../core/contexts/AppContext";

import {
    VehicleType,
    vehicleDataURL,
    FieldsMinMax,
} from "../../constants/constants";

// SCSS imports
import "../CaptchaValidationSection/CaptchaValidationSection.scss";
import "./RevisioniBody.scss";

// components import
import InputTextComponent from "../../../../../../shared/components/InputTextComponent/InputTextComponent";
import CaptchaValidationSection from "../CaptchaValidationSection/CaptchaValidationSection";
import { showDialogFailed } from "../../../../../../shared/utils/DialogUtils";
import ButtonWithIconAlt from "../../../../../../shared/components/buttons/ButtonWithIconAlt/ButtonWithIconAlt";
import RevisioniResults from "../RevisioniResults/RevisioniResults";
import SelectComponent from "../../../../../../shared/components/dropdowns/SelectComponent/SelectComponent";

// Reducer and State type import
import revisioniReducer, { RevisioniState } from "../../reducers/revisioniReducer/revisioniReducer";

// Stato iniziale
const initialState: RevisioniState = {
    plate: "",
    searchedPlate: "",
    plateLenError: "",
    vehicleType: "",
    searchedType: "",
    typeLenError: "",
    authCaptcha: "",
    vehicleData: undefined,
    vehicleDataSpinner: false,
    vehicleDataError: undefined,
};

/* RevisioniBody ha uno stato interno che tiene principalmente 
    traccia del valore dell'authorization-code del captcha (authCaptcha) 
    recuperato dal componente padre attraverso l'uso di una callback 
    e del value del field della targa (targa). Il componente in questione
    dati i valori sopracitati gestisce attraverso un bottone di submit il recupero
    delle informazioni inerenti il veicolo ricercato. */
const RevisioniBody = () => {
    const { appText } = useContext(AppContext);
    const { REVISIONI_TEXT, RESULTS_LABELS, VEHICLE_TYPES, REVISIONI_ERROR_MESSAGES } = appText;

    const [state, dispatch] = useReducer(revisioniReducer, initialState);
    const plateErrorRef = useRef<HTMLDivElement>(null);
    const typeErrorRef = useRef<HTMLDivElement>(null);
    
    const [clearButtonClicked, setClearButtonClicked] = useState(false);

    // Funzione del bottone "Svuta Ricerca": azzera i campi e riporta l'app allo stato iniziale
    function handleClearButtonClick() {
        dispatch({ type: "SET_AUTH_CAPTCHA", payload: "" });
        dispatch({ type: "SET_PLATE", payload: "" });
        dispatch({ type: "SET_TYPE", payload: "" });
        dispatch({ type: "SET_VEHICLE_DATA", payload: undefined });
        setClearButtonClicked(true);
    }

    // Salva nello stato l'auth-captcha che riceve da CaptchaValidationOutlinedPaper
    const handleAuthCaptcha = (str: string) => {
        dispatch({ type: "SET_AUTH_CAPTCHA", payload: str });
    };

    /* Aggiornamento dal componente figlio per la pulizia dello stato del click 
    del bottone pulisci */
    const handleOnClearExecuted = (val: boolean) => {
        setClearButtonClicked(val);
    };

    const handleSearch = () => {
        fetchVechicleData(
            state.authCaptcha,
            state.plate,
            state.vehicleType
        );
    };

    // Recupera al submit i dati del veicolo da mostrare
    function fetchVechicleData(
        authCaptcha: string,
        plate: string,
        vehicleType: string
    ) {
        dispatch({ type: "SET_VEHICLE_DATA", payload: undefined});
        if(!validateFields(authCaptcha, plate, vehicleType)) {     
            dispatch({ type: "SET_AUTH_CAPTCHA", payload: "" });
            setClearButtonClicked(true);
            return;
        }

        // setta lo spinner inviando al reduced il cambio di stato
        // e invoca la fetch vs il microservizio
        dispatch({ type: "SET_VEHICLE_DATA_SPINNER", payload: true });
        
        fetch(`${vehicleDataURL}/${vehicleType}/${plate.toUpperCase()}`, {
            method: "GET",
            headers: {
                guid: authCaptcha,
            },
        })
        // quando ritorna in modo asincrono la response esegui la sua gestione 
        // response.ok e' la 200
        .then((response) => {       
            //console.log(response);
            if (!response.ok) {
                throw new Error("Errore response fetch-vehicle-data");
            }
            return response.json();
        })
        // se response e' ok allora in modo asincrono attendi i dati
        // se i dati ritornano senza errore effettua i dispatch per il reducer
        .then((res) => {
            //console.log(res);
            if(!res.codice) {
                throw new Error("Errore response fetch-vehicle-data");
            }
            if (res.codice !== "200" ) {
                dispatch({ type: "SET_AUTH_CAPTCHA", payload: "" });
                setClearButtonClicked(true);
                showDialogFailed(
                    REVISIONI_TEXT.FETCH_VEICLE_DATA_ERROR_CODE, 
                    REVISIONI_ERROR_MESSAGES[`_${res.codice}`]
                );
                return;
            }
            // se sono presenti revisioni trasforma i dati ricevuti e li salva nel reducer
            if(res.informations.length){
                const data = translateObject(res);
                dispatch({ type: "SET_VEHICLE_DATA", payload: data });
            }
            const verboseVehicleType = VEHICLE_TYPES.filter((item: any) => {
                return item.value === vehicleType;
            })[0].label;

            dispatch({ type: "SET_VEHICLE_DATA_SPINNER", payload: false });
            dispatch({ type: "SET_AUTH_CAPTCHA", payload: "" });
            dispatch({ type: "SET_SEARCHED_PLATE", payload: state.plate });
            dispatch({ type: "SET_SEARCHED_TYPE", payload: verboseVehicleType });// DA MODIFICARE con api definitive
            setClearButtonClicked(true);
        })
        // se errori sulla fetch
        // effettua i dispatch per il reducer
        .catch((error) => {
            console.log(error);
            dispatch({ type: "SET_AUTH_CAPTCHA", payload: "" });
            setClearButtonClicked(true);
            dispatch({ type: "SET_VEHICLE_DATA_ERROR", payload: error });
            dispatch({ type: "SET_VEHICLE_DATA_SPINNER", payload: false });
            showDialogFailed(REVISIONI_TEXT.FETCH_VEICLE_DATA_ERROR_CODE, REVISIONI_TEXT.FETCH_VEICLE_DATA_ERROR_MSG);
        });
    }

    const translateObject = (receivedObject: any) => {
        
        const convertedObject = {
            esito: receivedObject["esito"],
            messaggio: receivedObject["messaggio"],

            inspections: receivedObject["informations"].map((licenceObj:any) => {
                
                const newInspections: {[k: string]: any} = {
                    date: fixDate(licenceObj["datRvs"]),
                    result: RESULTS_LABELS.filter((item: any) => {
                        return item.value === licenceObj["flgEsiRvsVei"];
                    })[0]?.label,
                    km: licenceObj["numKmlTotVei"] 
                        ? fixKilometers(licenceObj["numKmlTotVei"]) 
                        : fixKilometers(licenceObj["numKmiPcsRvs"]),
                    plate: licenceObj['plate']
                };
                
                return newInspections;
            })
            
            
          };
          return convertedObject;
    };

    const fixDate = (date: string) => {
        const inputDate: Date = new Date(date);
        const day: number = inputDate.getDate();
        const month: number = inputDate.getUTCMonth() + 1; // Add 1 since January is 0
        const year: number = inputDate.getUTCFullYear();

        return `${day.toString().padStart(2, "0")}/${month.toString().padStart(2, "0")}/${year}`;
    };
    
    const fixKilometers = (kilometers: string) => {
        const inputMeters: number = parseFloat(kilometers);
        if(Number.isNaN(inputMeters)){
            return "0";
        }
        if(inputMeters < 1000) {
            return(inputMeters).toString();
        }else {
            return (inputMeters / 1000).toLocaleString("en-US", {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
            });
        }
    };

    function isAlfanumeric(string: string) {
        var alphanumericRegex: RegExp = /^[a-zA-Z0-9]+$/;
        if (!alphanumericRegex.test(string)) {
          return false;
        }
        return true;
      }

    //verifica la validità dei campi 
    function validateFields (
        authCaptcha: string,
        plate: string,
        vehicleType: string
    ){
        let valid = true;
        // se input text non valorizzato visualizza finestra di errore
        // e ritorna immediatamente senza invocare ricerca
        if (authCaptcha===""){
            showDialogFailed(REVISIONI_TEXT.INVALID_CAPTCHA_ERROR_CODE, REVISIONI_TEXT.INVALID_CAPTCHA_ERROR_MSG);
            valid = false;
        }
        // verifica stato del campo targa: 
        // lughezza tra 7 e 8 altrimenti setta errore e ritorna
        let plateMin;
        let errorMsg = '';
        if(vehicleType === VehicleType.CICLOMOTORE){
            plateMin = FieldsMinMax.LICENCE_CICLOMOTORE_NUMBER_MIN;
            errorMsg = REVISIONI_TEXT.INVALID_CICLOMOTORE_PLATE_ERROR_MSG;
        }else{
            plateMin = FieldsMinMax.LICENCE_NUMBER_MIN;
            errorMsg = REVISIONI_TEXT.INVALID_PLATE_ERROR_MSG;
        }
        if(!isAlfanumeric(plate)) {
            errorMsg = REVISIONI_TEXT.INVALID_PLATE;
        }
        if (plate.length < plateMin || plate.length > FieldsMinMax.LICENCE_NUMBER_MAX || !isAlfanumeric(plate)) {
            showDialogFailed(REVISIONI_TEXT.INVALID_PLATE_ERROR_CODE, errorMsg);
            
            if (plateErrorRef.current) {
                plateErrorRef.current.style.display = "block";
            }
            dispatch({ type: "SET_PLATE_LEN_ERROR", payload: "Errore lunghezza targa" });
            // display toast message rimosso componente
            valid = false;
        }
        // verifica stato del campo tipo veicolo: 
        // se non è stato selezionato un tipo veicolo setta errore e ritorna
        if (vehicleType === "") {
            showDialogFailed(REVISIONI_TEXT.INVALID_TYPE_ERROR_CODE, REVISIONI_TEXT.INVALID_TYPE_ERROR_MSG);
            if (typeErrorRef.current) {
                typeErrorRef.current.style.display = "block";
            }
            dispatch({ type: "SET_TYPE_LEN_ERROR", payload: "Mancata selezione tipo veicolo" });
            // display toast message rimosso componente
            valid = false;
        }

        return valid;
    }



    /* handlePlateChange effettua un controllo sulla lunghezza del value scritto.
        Se quest'ultima è inferiore a 7 e diversa da 0, viene mostrato un messaggio
        di errore relativo alla targa del veicolo tramite l'aggiornamento di una 
        variabile di riferimento. In caso contrario, il messaggio di errore viene
        nascosto. */
    const handlePlateChange = (name: string, value: string) => {
        if (plateErrorRef.current) {
            plateErrorRef.current.style.display = "none";
        }
        dispatch({ type: "SET_PLATE", payload: value.trim() });
    };
    /* handleTypeChange in caso di errore mostra un messaggio riguardante la selezione
       del tipo di veicolo. Se non ci sono errori aggiorna il valore della tipo di veicolo*/
    const handleTypeChange = (name: string, value: string) => {
        if (typeErrorRef.current) {
            typeErrorRef.current.style.display = "none";
        }
        dispatch({ type: "SET_TYPE", payload: value });
    };


    return (
        <div className="RevisioniBody">
            <div className="RevisioniBody__container container ">
                <div className="row">
                    <h4 className="RevisioniBody__title ">
                        {REVISIONI_TEXT?.APP_BODY_TITLE ?? ""}
                    </h4>
                </div>
                <div className="row">
                    <div className="col-lg-6 mt-4">
                        {/** Select del tipo di veicolo */}
                        <SelectComponent
                            name="Select"
                            altDefaultOption={{
                                label: REVISIONI_TEXT?.VEHICLE_TYPE_FIELD_PLACEHOLDER ?? "", 
                                value: ""
                            }}
                            value={state.vehicleType}
                            state="success"
                            data={VEHICLE_TYPES}
                            onSelectOption={(name: string, value: string) =>
                                handleTypeChange(name, value)
                            }
                        />
                        <div
                            ref={typeErrorRef}
                            id="type-error"
                            className="RevisioniBody__textfield_hint_text"
                        >
                            {REVISIONI_TEXT?.INVALID_TYPE ?? ""}
                        </div>
                    </div>
                    <div className="col-lg-6 mt-4">
                        {/**Campo targa */}
                        <InputTextComponent
                            name="plate"
                            value={state.plate}
                            placeholder={`${REVISIONI_TEXT?.PLATE_FIELD_PLACEHOLDER ?? ""}*`}
                            onChange={(name: string, value: string) =>
                                handlePlateChange(name, value)
                            }
                            maxLength={8}
                            isRequired={false}
                        />
                        <div
                            ref={plateErrorRef}
                            id="plate-error"
                            className="RevisioniBody__textfield_hint_text"
                        >
                            {REVISIONI_TEXT?.INVALID_PLATE ?? ""}
                        </div>
                    </div>
                </div>
                <CaptchaValidationSection
                    onAuthCaptcha={handleAuthCaptcha}
                    onClearExecuted={handleOnClearExecuted}
                    isClearButtonClicked={clearButtonClicked}
                    vehicleData={state.vehicleData}
                />

                <div className="row">
                    <div className="col-12 col-sm-6 mt-3 d-flex justify-content-start">
                        {/**Bottone per avviare la ricerca */}
                        <ButtonWithIconAlt
                            size={"big --full-sm"}
                            icon={"bi bi-search"}
                            title={REVISIONI_TEXT?.SEARCH ?? ""}  
                            cbClick={handleSearch}  
                        />
                                                
                    </div>
                    <div className="col-12 col-sm-6 mt-3 d-flex justify-content-end">
                        {/** bottone per cancellare la ricerca e resetta il form */}
                        <ButtonWithIconAlt
                            size={"full-sm"}
                            type={"outline-primary"}
                            icon={"bi bi-arrow-counterclockwise"}
                            title={REVISIONI_TEXT?.CLEAR ?? ""}    
                            cbClick={handleClearButtonClick}
                        />
                    </div>
                </div>
                
            </div>
            {/* Se presenti nello stato i vehicleData, vengono mostrati i risultati
                    in una tab*/}
            {typeof state.vehicleData !== "undefined" &&
                 <RevisioniResults 
                    vehicleType={state.searchedType} 
                    vehicleData={state.vehicleData} 
                    plate={state.searchedPlate}
                /> 
            }
        </div>
    );
};

export default RevisioniBody;
